import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFrontendAuth } from '../hooks/useAuth';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import {
    confirmChildMeasurements,
    getMeasurementSuggestions,
    skipChildMeasurements,
    type CurrentMeasurementSuggestionDto,
} from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { useLoginPrompt } from '../loginPrompts/loginPromptContext';

/**
 * Home popup for accounts that already have a child.
 * Pre-fills an LMS prediction; confirm writes My Petito. Skip pauses fit until the next window.
 */
const PendingMeasurementPrompt = () => {
    const { isAuthenticated, isAuthReady } = useFrontendAuth();
    const { grantedId, reportWaiting, claim, settle } = useLoginPrompt();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();
    const { data: pending = [], isFetched } = useProtectedQuery<CurrentMeasurementSuggestionDto[]>({
        queryKey: ['measurementSuggestions'],
        queryFn: getMeasurementSuggestions,
        enabled: isAuthReady && isAuthenticated,
        staleTime: 60 * 1000,
    });

    const [active, setActive] = useState<CurrentMeasurementSuggestionDto | null>(null);
    const [height, setHeight] = useState('');
    const [weightGrams, setWeightGrams] = useState('');
    const [saving, setSaving] = useState(false);
    const [finished, setFinished] = useState(false);
    const eligible = isAuthenticated && isFetched && !finished && pending.length > 0;

    useEffect(() => {
        reportWaiting('child-measurements', eligible);
    }, [eligible, reportWaiting]);

    useEffect(() => {
        if (grantedId !== 'child-measurements' || active || !eligible) return;
        const next = pending[0];
        if (!next) return;
        claim('child-measurements');
        setActive(next);
        setHeight(next.suggestedHeightCm != null ? String(next.suggestedHeightCm) : '');
        setWeightGrams(
            next.suggestedWeightKg != null ? String(Math.round(next.suggestedWeightKg * 1000)) : ''
        );
    }, [grantedId, active, eligible, pending, claim]);

    if (!isAuthenticated || !active) return null;

    const range =
        active.typicalHeightLowCm != null && active.typicalHeightHighCm != null
            ? `${active.typicalHeightLowCm}–${active.typicalHeightHighCm} cm`
            : null;

    const close = () => setActive(null);

    const handleSkip = async () => {
        setSaving(true);
        try {
            await skipChildMeasurements(active.childId);
            await queryClient.invalidateQueries({ queryKey: ['measurementSuggestions'] });
            await queryClient.invalidateQueries({ queryKey: ['myChildren'] });
            setFinished(true);
            close();
            settle();
        } finally {
            setSaving(false);
        }
    };

    const handleConfirm = async () => {
        const h = height.trim() ? Number(height) : undefined;
        const grams = weightGrams.trim() ? Number(weightGrams) : undefined;
        setSaving(true);
        try {
            await confirmChildMeasurements(active.childId, {
                heightCm: h && !Number.isNaN(h) ? h : undefined,
                weightKg: grams && !Number.isNaN(grams) ? grams / 1000 : undefined,
            });
            await queryClient.invalidateQueries({ queryKey: ['measurementSuggestions'] });
            await queryClient.invalidateQueries({ queryKey: ['myChildren'] });
            await queryClient.invalidateQueries({ queryKey: ['recommendations'] });
            setFinished(true);
            close();
            settle();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Transition appear show as={Fragment}>
            <Dialog as="div" className="relative z-[80]" onClose={handleSkip}>
                <div className="fixed inset-0 bg-black/40" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-[var(--bg-color)] p-5 shadow-xl">
                            <Dialog.Title className="text-lg font-semibold text-[var(--text-color)]">
                                {tr('mypetito.measure.title', 'Does this still fit {name}?').replace(
                                    '{name}',
                                    active.childName
                                )}
                            </Dialog.Title>
                            <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                {tr(
                                    'mypetito.measure.body',
                                    'Suggested size from last measurements and age. Correct it if needed — this updates My Petito.'
                                )}
                                {active.suggestedPhaseName
                                    ? ` ${tr('mypetito.measure.phase', 'Phase: {phase}.').replace(
                                          '{phase}',
                                          active.suggestedPhaseName
                                      )}`
                                    : ''}
                                {range
                                    ? ` ${tr('mypetito.measure.typical', 'Typical for this age: {range}.').replace(
                                          '{range}',
                                          range
                                      )}`
                                    : ''}
                            </p>
                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <label className="text-sm">
                                    {tr('form.childmodal.height.label', 'Height (cm)')}
                                    <input
                                        value={height}
                                        onChange={(e) => setHeight(e.target.value)}
                                        inputMode="decimal"
                                        className="mt-1 w-full rounded-xl border border-[color-mix(in_srgb,var(--text-color),transparent_80%)] bg-white p-2.5"
                                    />
                                </label>
                                <label className="text-sm">
                                    {tr('form.childmodal.weight.label', 'Weight (grams)')}
                                    <input
                                        value={weightGrams}
                                        onChange={(e) => setWeightGrams(e.target.value)}
                                        inputMode="numeric"
                                        className="mt-1 w-full rounded-xl border border-[color-mix(in_srgb,var(--text-color),transparent_80%)] bg-white p-2.5"
                                    />
                                </label>
                            </div>
                            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => void handleSkip()}
                                    className="min-h-11 rounded-xl px-4 text-sm font-medium underline"
                                >
                                    {tr('mypetito.measure.skip', 'Not now')}
                                </button>
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => void handleConfirm()}
                                    className="min-h-11 rounded-xl bg-[var(--accent-color)] px-5 text-sm font-semibold text-white"
                                >
                                    {tr('mypetito.measure.confirm', 'Looks right')}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default PendingMeasurementPrompt;
